import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { User } from '@/types/user';

interface ProfileEditFormProps {
  user: User;
  onCancel: () => void;
  onSuccess: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  isUpdating: boolean;
}

/**
 * Profile edit form component for updating user profile information.
 */
export const ProfileEditForm: React.FC<ProfileEditFormProps> = ({
  user,
  onCancel,
  onSuccess,
  updateProfile,
  isUpdating,
}) => {
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email_address: user.email_address || '',
    country: user.country || '',
    city: user.city || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email_address: user.email_address || '',
      country: user.country || '',
      city: user.city || '',
    });
  }, [user]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email_address.trim()) {
      newErrors.email_address = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
      newErrors.email_address = 'Invalid email address';
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      await updateProfile({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email_address: formData.email_address.trim(),
        country: formData.country,
        city: formData.city.trim(),
      });
      onSuccess();
    } catch (error: any) {
      setSubmitError(error?.message || 'Failed to update profile. Please try again.');
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Edit Profile
      </Typography>

      <Card sx={{ borderRadius: 1, boxShadow: 1, maxWidth: 600, p: 3 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Box>

            <TextField
              label="First Name"
              fullWidth
              required
              value={formData.first_name}
              onChange={handleChange('first_name')}
              error={!!errors.first_name}
              helperText={errors.first_name}
              margin="normal"
              disabled={isUpdating}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                },
              }}
            />

            <TextField
              label="Last Name"
              fullWidth
              required
              value={formData.last_name}
              onChange={handleChange('last_name')}
              error={!!errors.last_name}
              helperText={errors.last_name}
              margin="normal"
              disabled={isUpdating}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                },
              }}
            />

            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={formData.email_address}
              onChange={handleChange('email_address')}
              error={!!errors.email_address}
              helperText={errors.email_address}
              margin="normal"
              disabled={isUpdating}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                },
              }}
            />

            <FormControl fullWidth required margin="normal" error={!!errors.country}>
              <InputLabel>Country</InputLabel>
              <Select
                value={formData.country}
                onChange={(e) => handleChange('country')({ target: { value: e.target.value } })}
                label="Country"
                disabled={isUpdating}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    '&:hover': { borderColor: 'primary.main' },
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <MenuItem value="Bulgaria">Bulgaria</MenuItem>
                <MenuItem value="USA">USA</MenuItem>
              </Select>
              {errors.country && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.country}
                </Typography>
              )}
            </FormControl>

            <TextField
              label="City"
              fullWidth
              required
              value={formData.city}
              onChange={handleChange('city')}
              error={!!errors.city}
              helperText={errors.city}
              margin="normal"
              disabled={isUpdating}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                },
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={onCancel}
                disabled={isUpdating}
                sx={{ minHeight: '44px', transition: 'all 0.2s ease-in-out' }}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={isUpdating}
                disabled={isUpdating}
                sx={{ minHeight: '44px', transition: 'all 0.2s ease-in-out' }}
              >
                Save Changes
              </LoadingButton>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
